import {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import {SymbolThematic} from "@esWebAPI/es/model/Thematic";
import {PositionAnchor} from "@esWebAPI/es/map/layer/azure/AzureLayer";
import {DataDrivenPropertyValueSpecification, ExpressionFilterSpecification} from "@maplibre/maplibre-gl-style-spec";
import {SymbolType} from "@esWebAPI/es/model/SymbolType";
import {Expression} from "azure-maps-control";

export class SymbolThematicStyler {

    /**
     * Build an expression returning the attribute value.
     */
    private getAttrValue(symbolThematic: SymbolThematic|undefined) {
        if(!symbolThematic) {
            throw new Error("getAttrValue called without SymbolThematic");
        }
        const symbolAttr = symbolThematic.attribute;
        if(symbolThematic.$type === "DistinctSymbolThematic") {
            return ['get', symbolAttr];
        }
        else if(symbolThematic.$type === "RangeSymbolThematic") {
            return ['to-number', ['get', symbolAttr]];
        }
        else if(symbolThematic.$type === "NoneSymbolThematic") {
            return ['get', symbolAttr];
        }
        throw new Error("Unsupported symbol thematic type: " + symbolThematic?.$type)
    }

    getExpression(layerView: ILayerView, layerInfo: LayerInfo, symbolThematic?: SymbolThematic): DataDrivenPropertyValueSpecification<string> {
        const symbolExp: any = [];

        const symbolStyle = layerView.styleSettings.defaultStyle.$type === "SymbolAndPolygonStyle" ?
            layerView.styleSettings.defaultStyle.symbolStyle : layerView.styleSettings.defaultStyle;

        if (layerView.styleSettings?.symbolThematicSettings?.enabled && symbolThematic) {

            if (symbolThematic.$type === "NoneSymbolThematic") {
                return String(symbolStyle.symbolTypeId);
            }

            const valEx = this.getAttrValue(symbolThematic);

            symbolExp.push('case')

            //Ranges
            const ranges = symbolThematic.ranges;
            const attrType = layerInfo.attributes.find(attr => attr.name === symbolThematic?.attribute)?.attrType;
            const isNumericAttribute = attrType === "DECIMAL" || attrType === "INTEGER" ||attrType === "TIMESTAMP";
            ranges.forEach((r: any, i:number) => {
                let op = i < ranges.length - 1 ? '<=' : '>';
                op = isNumericAttribute ? op : "=="
                let val = r.val;
                if(isNumericAttribute) {
                    val = +r.val;
                } else if(attrType === "BOOLEAN") {
                    val = r.val.toLowerCase() == "true";
                }
                symbolExp.push([op, valEx, val]); // Condition
                symbolExp.push(String(r.symbolTypeId)); // Value
                if (val === '' && op === '==') { // op should always be == for empty string
                    symbolExp.push([op, valEx, null]); // Condition
                    symbolExp.push(String(r.symbolTypeId)); // Value
                }
            });
            symbolExp.push(String(symbolStyle.symbolTypeId));
            return symbolExp;
        }

        return String(symbolStyle.symbolTypeId);
    }

    getAnchor(layerView: ILayerView, layerInfo: LayerInfo, symbolTypes: SymbolType[], symbolThematic?: SymbolThematic): DataDrivenPropertyValueSpecification<PositionAnchor>  {
        const bottomAnchorIds = symbolTypes.filter(st => st.offsetY < -0.5).map(st => st.id);
        const leftAnchorIds = symbolTypes.filter(st => st.offsetX > -0.25).map(st => st.id);
        if (bottomAnchorIds.length > 0 || leftAnchorIds.length > 0) {

            const symbolExpression = this.getExpression(layerView, layerInfo, symbolThematic);

            //TODO USE LET VAR for symbolExpression;

            return [
                'case',
                ['all', ['in', ['to-number', this.getExpression(layerView, layerInfo, symbolThematic)], ['literal', bottomAnchorIds]], ['in', ['to-number', symbolExpression], ['literal', leftAnchorIds]]],
                "bottom-left",
                ['in', ['to-number', symbolExpression], ['literal', bottomAnchorIds]],
                "bottom",
                ['in', ['to-number', symbolExpression], ['literal', leftAnchorIds]],
                "left",
                "center"
            ];
        }
        return "center";
    }

    getThematic(layerView: ILayerView): Promise<SymbolThematic|undefined> {
        const overrideThematic = layerView.styleSettings.symbolThematicSettings?.overrideThematic;

        let symbolPromise: Promise<SymbolThematic | undefined>;

        if (overrideThematic) {
            symbolPromise = Promise.resolve(overrideThematic);
        } else if (layerView.styleSettings.symbolThematicSettings?.enabled) {
            symbolPromise = es.api.thematic.getSymbolThematic(layerView.id, layerView.styleSettings.symbolThematicSettings.attributeName, {noCache: true});
        } else {
            symbolPromise = Promise.resolve(undefined);
        }

        return symbolPromise;
    }

    getFilterExpression(layerView: ILayerView, symbolThematic?: SymbolThematic): ExpressionFilterSpecification | undefined {
        if (!symbolThematic || !layerView.styleSettings.symbolThematicSettings?.enabled) {
            return undefined;
        }

        const rangesToHide = symbolThematic.ranges.filter(t => t.hideFeatures).flatMap(t => t.val === '' ? ['', null] : t.val);
        if (rangesToHide.length === 0) {
            // all color ranges are being shown. No need to create filter;
            return undefined;
        }

        if (symbolThematic.$type === "DistinctSymbolThematic") {
            return ['!', ['in', ['get', symbolThematic.attribute], ['literal', rangesToHide]]];
        }

        if (symbolThematic.$type === "RangeSymbolThematic") {

            const attribute = symbolThematic.attribute;
            const rangesFilters: Expression[] = [];

            if (symbolThematic.ranges[0].hideFeatures) {
                rangesFilters.push(['<=', ['get', attribute], +symbolThematic.ranges[0].val]);
            }

            for (let i = 1; i < symbolThematic.ranges.length - 1; i++){
                const range = symbolThematic.ranges[i];
                const prevRange = symbolThematic.ranges[i-1];
                if(!range.hideFeatures) {
                    continue;
                }
                rangesFilters.push(['all', ['<=', ['get', attribute], +range.val], ['>', ['get', attribute], +prevRange.val]]);
            }

            const lastRange = symbolThematic.ranges[symbolThematic.ranges.length -1];
            if(lastRange.hideFeatures) {
                rangesFilters.push(['>', ['get', attribute], +lastRange.val])
            }

            return ['!', ['any', ...rangesFilters]];
        }
    }
}